import { useContext } from 'react';
import styled from '@emotion/styled';
import { Box, darken, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { selectIngestManagementRecentTitles } from '@/redux/selectors/ingestManagementSelectors';
import { getLineageMetaTitle } from '@/_utils/utils';
import { SelectedTitlesV2 } from '@/components/IngestManagement/FileIngest/TitleAssociationV2/TitleAssociationV2';

type RecentTitlesProp = {
  selectedTitle?: number;
  onRecentTitleClick: (titles: SelectedTitlesV2) => void;
};

const RecentTitles = ({ selectedTitle, onRecentTitleClick }: RecentTitlesProp) => {
  const { disableRecentTitles, versionRender } = useContext(ConfigContext).ingestManagementConfig;
  const recentTitles = !disableRecentTitles ? useAppSelector(selectIngestManagementRecentTitles) : [];

  if (disableRecentTitles || recentTitles.length === 0) return null;

  return (
    <Wrapper>
      <Header>Recently Selected Titles</Header>
      <Content>
        {recentTitles.map((titles) => {
          if (!titles || !Array.isArray(titles)) return null;
          const curVersion = titles.at(-1)?.value?.entity_type === 'version' ? titles.at(-1)?.value : null;
          const curTitle = curVersion ? (titles.at(-2)?.value ?? undefined) : (titles.at(-1)?.value ?? undefined);
          return (
            <RecentTitle
              key={titles.at(-1)?.value?.id}
              active={titles.at(-1)?.value?.id === selectedTitle}
              onClick={() => onRecentTitleClick(titles as SelectedTitlesV2)}
            >
              <Typography>
                <strong>{curTitle?.name}</strong>
                {curVersion && (
                  <Typography variant="caption">
                    {` | ${typeof versionRender !== 'undefined' ? versionRender(curVersion) : curVersion.name}`}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" sx={{ lineHeight: 1.9, fontSize: '.9rem' }}>
                {getLineageMetaTitle(curTitle, true)}
              </Typography>
            </RecentTitle>
          );
        })}
      </Content>
    </Wrapper>
  );
};

export default RecentTitles;

const Wrapper = styled.div(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2)
}));

const Header = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  marginBottom: theme.spacing(2)
}));

const Content = styled.div(({ theme }) => ({
  width: '100%',
  display: 'flex',
  gap: theme.spacing(1),
  overflow: 'auto'
}));

const RecentTitle = styled(Box, { shouldForwardProp: (prop) => !['active'].includes(prop) })<{ active: boolean }>(({ theme, active }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  minWidth: 300,
  cursor: active ? 'default' : 'pointer',
  background: active ? darken(theme.palette.background.paper, 0.2) : 'inherit',
  '&:hover': {
    background: darken(theme.palette.background.paper, 0.2)
  }
}));
